import { RefObject, useMemo } from "react";
// Third Party
import moment from "moment";

// MUI
import { GridColDef, GridFilterInputValue, getGridStringOperators, GridFilterOperator, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { GridApi } from "@mui/x-data-grid-premium";
import clsx from "clsx";
import { Stack } from "@mui/material";
import { GridGroupingColDefOverride } from "@mui/x-data-grid-premium";
// Components
import { TypographyHeader } from "@Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Helpers
import { getFormatDate } from "@Common/helpers/Dates";
import { getPinnedCellClassName } from "@/Layouts/components/Tables/DataSmartTable/helpers/";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Components
import { ExpandCollapseAllButtonHeader } from "@Layouts/components/TreeGrid/ExpandCollapseAllButtonHeader";
import { CustomGridTreeDataGroupingCell } from "@Layouts/components/TreeGrid/CustomGridTreeDataGroupingCell";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
const HEADER_CLASS_NAME = "table-header-top-aligned";

//Layouts
import { CustomGridTreeDataGroupingCellProps } from "@/Layouts/components/types";

// Assets
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";

const notEqualsOperator: GridFilterOperator<any, string> = {
	label: "Not equal",
	value: "notEquals",
	getApplyFilterFn: (filterItem) => {
		// if the filter isn’t fully defined, don’t apply anything
		if (!filterItem.field || !filterItem.value || !filterItem.operator) {
			return null;
		}
		return (cellValue) => {
			// only truthy when the cellValue is different from filterItem.value
			return cellValue != null && cellValue.toLocaleLowerCase() !== filterItem.value.toLocaleLowerCase();
		};
	},
	// reuse the default text‐input for entering the filter value
	InputComponent: GridFilterInputValue,
};

const getClassNames = () => {
	return clsx({
		"group-[.odd-row]/stripe:!bg-[#F7F7F7]": true,
		"group-[.even-row]/stripe:!bg-white": true,
		"group-[.table-footer]/footer:!bg-[#2357BE] ": true,
	});
};

const getBaseColumnDefinition = (): Partial<GridColDef> => ({
	align: "left",
	resizable: false,
	headerClassName: HEADER_CLASS_NAME,
	renderHeader: (params) => <TypographyHeader params={params} />,
	cellClassName: getClassNames(),
});

const getColumnDefinitions = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => [
	{ field: "_id", headerName: "ID", type: "string", filterable: false },
	...getPinnedColumns(baseColumnDefinition),
	...getOtherColumns(baseColumnDefinition),
];
export const groupingColDef: GridGroupingColDefOverride = {
	width: 48,
	maxWidth: 48,
	minWidth: 48,
	resizable: false,
	headerName: "Group",
	renderHeader: () => (
		<Stack alignItems="center" justifyContent="center" className="relative w-7 h-9">
			<ExpandCollapseAllButtonHeader className="left-[1px]" />
		</Stack>
	),
	cellClassName: (param) => {
		return getPinnedCellClassName(param, param.rowNode.depth ? "[background:unset_!important] group-child" : "");
	},
	colSpan: (value) => (!value ? 2 : 1),
	renderCell: (params) => {
		return (
			<div className="pt-[2px]">
				<CustomGridTreeDataGroupingCell {...(params as CustomGridTreeDataGroupingCellProps)} />
			</div>
		);
	},
	getApplyQuickFilterFn: () => null,
};
const getPinnedColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => [
	{
		...baseColumnDefinition,
		field: "saleDate",
		headerName: "Deal Date",
		width: 100,
		valueGetter: (value: Date) => value && getDateInUTC(new Date(moment.utc(value).format(DATE_FORMAT.monthAbbrDate))),
		valueFormatter: (value: Date) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		field: "saleType",
		headerName: "Sale Type",
		width: 60,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		field: "dealType",
		headerName: "Deal Type",
		width: 60,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		field: "dealNumber",
		headerName: "Deal #",
		width: 80,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		field: "dealStatus",
		headerName: "Deal Status",
		width: 90,
		minWidth: 90,
		maxWidth: 90,
		filterOperators: [...getGridStringOperators(), notEqualsOperator],
		renderCell: (params: GridRenderCellParams) => {
			if (params.rowNode?.depth > 0) return null;
			if (!params.value) {
				return undefined;
			}
			return (
				<TypographyCell params={params}>
					<CircleRoundedIcon
						className={`deal-status-${params.value} `}
						sx={{
							fontSize: "12px",
							marginTop: "-3px",
							marginRight: "5px",
						}}
					/>
					{params.value}
				</TypographyCell>
			);
		},
	},
	{
		...baseColumnDefinition,
		field: "stockNumber",
		headerName: "Stock#",
		width: 80,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		headerName: "Sales Associate",
		field: "salesEmployeeName",
		width: 100,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		field: "dealManagerName",
		headerName: "Sales Manager",
		width: 100,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		field: "financeManagerName",
		headerName: "Finance Manager",
		width: 100,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		field: "vehicleVin",
		headerName: "VIN",
		width: 140,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		...baseColumnDefinition,
		field: "units",
		headerName: "Units",
		width: 60,
		renderCell: (params) => (params.rowNode?.depth > 0 ? null : <TypographyCell params={params} />),
	},
	{
		field: "__empty",
		headerName: "",
		sortable: false,
		resizable: false,
		disableReorder: true,
		width: 12,
		minWidth: 12,
		maxWidth: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
		renderCell: () => <TypographyCell className={"!bg-blue"} />,
	},
];

const getOtherColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => [
	{
		...baseColumnDefinition,
		field: "invitationStatus",
		headerName: "Invitation Status",
		width: 90,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "firstName",
		headerName: "First Name",
		width: 80,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "lastName",
		headerName: "Last Name",
		width: 80,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "email",
		headerName: "Email",
		width: 180,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "invitationDateCreated",
		headerName: "Invitation Created",
		type: "date",
		width: 120,
		valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.usDateTime12hPadded),
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "invitationExpirationDate",
		headerName: "Invitation Expired",
		type: "date",
		width: 120,
		valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.usDateTime12hPadded),
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "vehicleYear",
		headerName: "Vehicle Year",
		width: 80,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "vehicleMake",
		headerName: "Make",
		width: 120,
		renderCell: (params) => <TypographyCell params={params} />,
	},
	{
		...baseColumnDefinition,
		field: "vehicleModel",
		headerName: "Model",
		width: 120,
		renderCell: (params) => <TypographyCell params={params} />,
	},
];

export const useConsumerRegistrationStatusColumns = (apiRef: RefObject<GridApi>): GridColDef[] => {
	const baseColumnDefinition = getBaseColumnDefinition();

	const columns = useMemo(() => getColumnDefinitions(baseColumnDefinition), [apiRef]);

	return columns;
};
